import React, { useCallback, useRef, useState } from "react";

import { FileAudio, Upload, X } from "lucide-react";
import { useDispatch } from "react-redux";

import { startTranscriptionSummaryTask } from "@/redux/transcription-summary/index";
import { store } from "@/store";

import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";

import { showError } from "@utils/notification";
import { cn } from "@utils/ui";
import type { UploadProgress } from "@utils/uploadWithProgress";

const ACCEPTED_EXTENSIONS = ".mp3,.wav,.mp4,.m4a";
const MAX_SIZE_BYTES = 500 * 1024 * 1024; // 500MB (matches the modal's hint)

// Only 2 and 3 have a defined role list on the batch diarization side
// (transcribe-summarize-api's speaker_roles.py DEFAULT_ROLE_ORDER_BY_COUNT) --
// no point offering a count the backend can't do anything useful with.
type ExpectedSpeakerCount = 2 | 3 | null;

// Paired with "en" server-side (AssemblyAI's language_codes code-switching
// requires exactly two codes, one of which must be "en") -- mirrors the
// whitelist in backend/api/transcription/handler.py and
// transcribe-summarize-api's languages.py. Only offered when a recording
// genuinely switches between English and one other language mid-conversation;
// leave unset ("Auto-detect") for anything else, including single-language
// non-English recordings, which universal-3-5-pro already handles natively.
const SECOND_LANGUAGE_OPTIONS: { code: string; label: string }[] = [
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "hi", label: "Hindi" },
  { code: "zh", label: "Mandarin" },
];

/** Human-readable megabytes, e.g. 40265318 → "38.4 MB". */
const formatMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

type AudioUploaderProps = {
  caseNumber: string;
  examinee_name: string; // Add examinee_name as a required prop
  disabled?: boolean;
  onRequestOverwriteConfirm?: () => Promise<boolean> | boolean;
  /**
   * "header" (default) renders the compact top-bar button; "empty-state"
   * renders the larger centered CTA used in the no-content panel (MDRS-113).
   */
  variant?: "header" | "empty-state";
};
export const AudioUploader = ({
  caseNumber,
  examinee_name,
  disabled,
  onRequestOverwriteConfirm,
  variant = "header",
}: AudioUploaderProps) => {
  const isEmptyState = variant === "empty-state";
  const dispatch = useDispatch<typeof store.dispatch>();

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modal + upload state (MDRS-113 Upload Recording modal).
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [expectedSpeakerCount, setExpectedSpeakerCount] =
    useState<ExpectedSpeakerCount>(null);
  const [secondLanguageCode, setSecondLanguageCode] = useState<string | null>(null);

  // Open the modal — gated by the same name check + overwrite confirmation the
  // header/empty-state CTAs used before the modal existed.
  const handleOpen = useCallback(async () => {
    if (disabled || isUploading) return;

    if (!examinee_name.trim()) {
      showError("Examinee name is required for transcription", dispatch);
      return;
    }

    if (onRequestOverwriteConfirm) {
      const shouldContinue = await onRequestOverwriteConfirm();
      if (!shouldContinue) return;
    }

    setSelectedFile(null);
    setProgress(null);
    setExpectedSpeakerCount(null);
    setSecondLanguageCode(null);
    setIsOpen(true);
  }, [disabled, dispatch, examinee_name, isUploading, onRequestOverwriteConfirm]);

  const validateFile = useCallback(
    (file: File) => {
      const name = file.name.toLowerCase();
      const okExtension = ACCEPTED_EXTENSIONS.split(",").some((ext) =>
        name.endsWith(ext.trim())
      );
      if (!okExtension) {
        showError("Please upload an MP3, MP4, WAV, or M4A file", dispatch);
        return false;
      }
      if (file.size > MAX_SIZE_BYTES) {
        showError("File size should not exceed 500MB", dispatch);
        return false;
      }
      return true;
    },
    [dispatch]
  );

  const handleFileSelected = useCallback(
    (file: File | undefined | null) => {
      if (!file) return;
      if (!validateFile(file)) return;
      setSelectedFile(file);
    },
    [validateFile]
  );

  const handleUpload = useCallback(async () => {
    if (!selectedFile || isUploading) return;

    setIsUploading(true);
    setProgress({ loaded: 0, total: selectedFile.size, percent: 0 });

    const formData = new FormData();
    formData.append("files", selectedFile, selectedFile.name);
    formData.append("case_number", caseNumber);
    formData.append("examinee_name", examinee_name);
    if (expectedSpeakerCount != null) {
      formData.append("expected_speaker_count", String(expectedSpeakerCount));
    }
    if (secondLanguageCode) {
      formData.append("second_language_code", secondLanguageCode);
    }

    try {
      await dispatch(
        startTranscriptionSummaryTask(formData, {
          onUploadProgress: setProgress,
        })
      );
      // The thunk starts polling + surfaces its own success toast; close the
      // modal once the bytes are up and the task is queued.
      setIsOpen(false);
    } catch {
      // The thunk shows the error toast; keep the modal open so the user can
      // retry or cancel.
    } finally {
      setIsUploading(false);
    }
  }, [
    caseNumber,
    dispatch,
    examinee_name,
    expectedSpeakerCount,
    isUploading,
    secondLanguageCode,
    selectedFile,
  ]);

  // Block dismissing the modal mid-upload (the request would keep running).
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isUploading) return;
      setIsOpen(open);
      if (!open) {
        setSelectedFile(null);
        setProgress(null);
        setExpectedSpeakerCount(null);
        setSecondLanguageCode(null);
      }
    },
    [isUploading]
  );

  const percent = progress?.percent ?? 0;

  return (
    <div>
      <Button
        variant="outline"
        size={isEmptyState ? "default" : "sm"}
        disabled={disabled}
        className={cn(
          "flex items-center gap-1.5 border-primary bg-primary-50 text-primary hover:bg-primary-50/80 hover:text-primary",
          isEmptyState && "gap-2 rounded-lg px-5 py-3 text-sm font-semibold"
        )}
        onClick={handleOpen}
      >
        <Upload />
        Upload Recording
      </Button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="max-w-[724px] gap-6 p-6"
          onInteractOutside={(e) => isUploading && e.preventDefault()}
          onEscapeKeyDown={(e) => isUploading && e.preventDefault()}
        >
          <div className="flex items-start justify-between">
            <DialogTitle className="text-2xl font-semibold text-background-dark">
              Upload Recording
            </DialogTitle>
            <DialogDescription className="sr-only">
              Upload an existing audio recording to transcribe and summarise for this
              case. Supported formats: MP3, MP4, WAV, M4A. Max size 500MB.
            </DialogDescription>
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              disabled={isUploading}
              aria-label="Close"
              className="text-foreground-muted transition-colors hover:text-foreground disabled:opacity-40"
            >
              <X className="size-6" />
            </button>
          </div>

          {isUploading || (progress && selectedFile) ? (
            // ── Uploading state ──
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-primary-50 text-primary">
                  <FileAudio className="size-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">
                    {selectedFile?.name}
                  </p>
                  <p className="text-xs text-foreground-muted">
                    Uploading audio file...
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="h-2 w-full overflow-hidden rounded-full bg-primary-50">
                  <div
                    className="h-full rounded-full bg-primary transition-[width]"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-foreground-muted">
                  <span>
                    {percent}% - {formatMb(progress?.loaded ?? 0)} of{" "}
                    {formatMb(progress?.total ?? selectedFile?.size ?? 0)}
                  </span>
                  <span>Uploading...</span>
                </div>
              </div>
            </div>
          ) : (
            // ── Default (dropzone) state ──
            <div className="flex flex-col gap-4">
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  handleFileSelected(e.dataTransfer.files?.[0]);
                }}
                className={cn(
                  "flex flex-col items-center gap-4 rounded-lg border border-dashed px-6 py-8 text-center transition-colors",
                  isDragging ? "border-primary bg-primary-50/50" : "border-border"
                )}
              >
                <span className="grid size-9 place-items-center rounded-full bg-background-muted text-foreground-muted">
                  <Upload className="size-4" />
                </span>
                <p className="text-sm text-foreground">
                  {selectedFile ? (
                    <span className="font-medium">{selectedFile.name}</span>
                  ) : (
                    <>
                      Drag and drop your recording or click &ldquo;Select File&rdquo;
                      to upload from your computer.
                    </>
                  )}
                </p>
                <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                  Select File
                </Button>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-foreground">
                  How many people are speaking? (optional)
                </span>
                <div className="flex flex-row items-center gap-6">
                  <RadioGroup
                    value={
                      expectedSpeakerCount != null
                        ? String(expectedSpeakerCount)
                        : ""
                    }
                    onValueChange={(val) =>
                      setExpectedSpeakerCount(val ? (Number(val) as 2 | 3) : null)
                    }
                    className="flex flex-row items-center gap-6"
                  >
                    <label className="flex cursor-pointer items-center space-x-2">
                      <RadioGroupItem value="2" id="expected-speaker-count-2" />
                      <span className="text-sm">2</span>
                    </label>
                    <label className="flex cursor-pointer items-center space-x-2">
                      <RadioGroupItem value="3" id="expected-speaker-count-3" />
                      <span className="text-sm">3 (with interpreter)</span>
                    </label>
                  </RadioGroup>
                  <button
                    type="button"
                    onClick={() => setExpectedSpeakerCount(null)}
                    className="text-sm text-foreground-muted underline-offset-2 hover:underline"
                  >
                    Not sure
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span
                  id="second-language-label"
                  className="text-sm font-medium text-foreground"
                >
                  Does the recording switch between English and another language?
                  (optional)
                </span>
                <Select
                  value={secondLanguageCode ?? "auto"}
                  onValueChange={(val) =>
                    setSecondLanguageCode(val === "auto" ? null : val)
                  }
                >
                  <SelectTrigger
                    className="w-[240px]"
                    aria-labelledby="second-language-label"
                  >
                    <SelectValue placeholder="Auto-detect" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto-detect</SelectItem>
                    {SECOND_LANGUAGE_OPTIONS.map(({ code, label }) => (
                      <SelectItem key={code} value={code}>
                        English + {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              className="border-primary bg-primary-50 text-primary hover:bg-primary-50/80 hover:text-primary"
              disabled={isUploading}
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={!selectedFile || isUploading}
              isLoading={isUploading}
              onClick={handleUpload}
            >
              {isUploading ? "Processing..." : "Upload Recording"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <input
        type="file"
        ref={fileInputRef}
        onChange={(e) => handleFileSelected(e.target.files?.[0])}
        accept={ACCEPTED_EXTENSIONS}
        style={{ display: "none" }}
      />
    </div>
  );
};
